// Standard Status Options
export const STATUS_OPTIONS = [
  { value: "Yes", label: "Yes (Implemented)", colors: { bg: '#e8f5e9', text: '#2e7d32' } },
  { value: "No", label: "No (Not implemented)", colors: { bg: '#ffebee', text: '#d32f2f' } },
  { value: "Not Applicable", label: "Not Applicable", colors: { bg: '#e8eaed', text: '#5f6368' } },
  { value: "Need Action", label: "Need Action", colors: { bg: '#fff8e1', text: '#f57c00' } },
];

// Standard YES / NO option
export const STANDARD_STATUS_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Standard Pending/Completed Status
export const STANDARD_PENDING_COMPLETED_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Completed" },
];

// Policy Status Options
export const POLICY_STATUS_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" }
];

// Document Type Options
export const DOCUMENT_TYPE_OPTIONS = [
  { value: "Policy", label: "Policy" },
  { value: "Document", label: "Document" },
  { value: "Form", label: "Form" },
  { value: "Image", label: "Image" },
  { value: "Video", label: "Video" },
  { value: "Quiz", label: "Quiz" },
  { value: "Other", label: "Other" }
];

// Document Center Folder View - Category groupings
export const DOCUMENT_CATEGORY_OPTIONS = [
  { value: "Policy",   label: "Policy"   },
  { value: "Document", label: "Document" },
  { value: "Evidence", label: "Evidence" },
];

export const DOCUMENT_CATEGORY_TYPE_MAP = {
  Policy:   ["Policy"],
  Document: ["Document", "Form"],
  Evidence: ["Image", "Video", "Quiz", "Other"],
};

/** Sort order for Policy / Document / Evidence group headers. */
export const DOCUMENT_CATEGORY_SORT_ORDER = ["Policy", "Document", "Evidence"];

export function getCategoryFromType(type) {
  for (const [cat, types] of Object.entries(DOCUMENT_CATEGORY_TYPE_MAP)) {
    if (types.includes(type)) return cat;
  }
  return "Policy";
}

/** Chip / header colors for Related Policy category groups. */
export function getDocumentCategorySx(category) {
  if (category === "Evidence") {
    return { bgcolor: "#FEF3C7", color: "#92400E", headerColor: "#92400E" };
  }
  if (category === "Document") {
    return { bgcolor: "#F0FDFA", color: "#0F766E", headerColor: "#0F766E" };
  }
  // Policy (default)
  return { bgcolor: "#EBEEFF", color: "#1D3DFC", headerColor: "#1D3DFC" };
}

/** Workflow status for Related Policy tagging (policy table + in-flight policy_center draft). */
export function getRelatedPolicyWorkflowStatus(policy) {
  if (!policy) return 'Approved';
  if (policy.has_active_draft && policy.active_draft_status) {
    return policy.active_draft_status;
  }
  if (policy.has_active_draft) return 'Draft';
  const explicit = String(policy.status || policy.workflow_status || '').trim();
  if (explicit) return explicit;
  return 'Approved';
}

export function isUnstableRelatedPolicyStatus(status) {
  const s = String(status || '').trim().toLowerCase();
  // Only Approved is considered stable for control tagging.
  return s !== 'approved';
}

export function getRelatedPolicyStatusChipSx(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pending approval') {
    return { backgroundColor: '#FFFBEB', color: '#B45309' };
  }
  if (s === 'draft') {
    return { backgroundColor: '#F3F4F6', color: '#4B5563' };
  }
  if (s === 'approved') {
    return { backgroundColor: '#ECFDF5', color: '#047857' };
  }
  return { backgroundColor: '#EBEEFF', color: '#1D3DFC' };
}

// Industry Options
export const INDUSTRY_OPTIONS = [
  { value: "Financial Services (e.g., banking, insurance, investment)", label: "Financial Services (e.g., banking, insurance, investment)" },
  { value: "Healthcare and Medical Services", label: "Healthcare and Medical Services" },
  { value: "Charity and Non-Profit Organizations", label: "Charity and Non-Profit Organizations" },
  { value: "E-commerce and Online Retail", label: "E-commerce and Online Retail" },
  { value: "Information Technology and Software Development", label: "Information Technology and Software Development" },
  { value: "Education and Training Services", label: "Education and Training Services" },
  { value: "Professional Services (e.g., legal, accounting, consulting)", label: "Professional Services (e.g., legal, accounting, consulting)" },
  { value: "Other", label: "Other" }
];

// Policy Domains — topical areas a policy template covers
export const POLICY_DOMAIN_OPTIONS = [
  { value: "Information Security", label: "Information Security" },
  { value: "Data", label: "Data" },
  { value: "Child Safety", label: "Child Safety" },
  { value: "Privacy", label: "Privacy" },
  { value: "Cybersecurity", label: "Cybersecurity" },
  { value: "Governance", label: "Governance" },
  { value: "Risk Management", label: "Risk Management" },
  { value: "Business Continuity", label: "Business Continuity" },
  { value: "HR/People", label: "HR/People" },
  { value: "Physical Security", label: "Physical Security" },
  { value: "Vendor/Third Party", label: "Vendor/Third Party" },
  { value: "AI/Emerging Tech", label: "AI/Emerging Tech" },
  { value: "Health & Safety", label: "Health & Safety" },
  { value: "Environmental & Sustainability", label: "Environmental & Sustainability" },
  { value: "Legal & Regulatory", label: "Legal & Regulatory" },
  { value: "Financial & Fraud", label: "Financial & Fraud" },
  { value: "Communications & Brand", label: "Communications & Brand" },
  { value: "Change & Operations", label: "Change & Operations" },
  { value: "Training & Competency", label: "Training & Competency" },
  { value: "Quality Management", label: "Quality Management" },
  { value: "Other", label: "Other" },
];

// Template Category Options
export const TEMPLATE_CATEGORY_OPTIONS = [
  { 
    value: "Standard", 
    label: "Standard", 
    description: "Generic templates that apply to most organizations",
    icon: "CategoryIcon",
    colors: { bg: '#EBF5FF', text: '#1D3DFC' },
    consultantMessage: "Standard templates provide a solid foundation for organizations in any industry. They cover essential cybersecurity controls that are relevant to all businesses."
  },
  { 
    value: "HIB", 
    label: "HIB Specify", 
    description: "Templates specifically optimized for healthcare organizations",
    icon: "HealthAndSafetyIcon",
    colors: { bg: '#E6F7EB', text: '#0D9F4F' },
    consultantMessage: "These templates are tailored for Healthcare Industry Bodies (HIB) and include specialized controls for patient data protection and healthcare-specific compliance requirements."
  },
  { 
    value: "DPE", 
    label: "DPE Specify", 
    description: "Templates specifically optimized for data protection",
    icon: "PublicIcon",
    colors: { bg: '#E6F0F9', text: '#2E90FA' },
    consultantMessage: "Data Protection Enhanced (DPE) templates focus on organizations handling critical data, including personal information. These templates emphasize controls for data protection compliance and privacy standards."
  }
];

// Training Category Options
export const TRAINING_CATEGORY_OPTIONS = [
  { value: "Cybersecurity", label: "Cybersecurity" },
  { value: "Data Protection", label: "Data Protection" },
];

// Training Status Options
export const TRAINING_STATUS_OPTIONS = [
  { value: "Not Started", label: "Not Started" },
  { value: "Not Required", label: "Not Required" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" }
];

//Control Category Options
export const CYBERESSENTIAL_CATEGORY_OPTIONS = [
  { value: "Assets", label: "Assets" },
  { value: "Secure/protect", label: "Secure/Protect" },
  { value: "Update", label: "Update" },
  { value: "Backup", label: "Backup" },
  { value: "Respond", label: "Respond" },]

export const CONTROL_CATEGORY_OPTIONS = [
  { value: "Cybersecurity", label: "Cybersecurity" },
  { value: "Data Protection", label: "Data Protection" },
  { value: "Common Cyber & Data Security", label: "Common Cyber & Data Security" },
];

//Control Domain Options
export const CONTROL_DOMAIN_OPTIONS = [
  { value: "People", label: "People" },
  { value: "Hardware And Software", label: "Hardware and Software" },
  { value: "Data", label: "Data" },
  { value: "Virus And Malware Protection", label: "Virus and Malware Protection" },
  { value: "Access Control", label: "Access Control" },
  { value: "Secure Configuration", label: "Secure Configuration" },
  { value: "Software Update", label: "Software Update" },
  { value: "Backup Essential Data", label: "Backup Essential Data" },
  { value: "Incident Response", label: "Incident Response" },
  { value: "Vendors", label: "Vendors" },
]



//Asset type Options
export const ASSET_TYPE_OPTION = [
  { value: "Desktop", label: "Desktop" },
  { value: "Laptop", label: "Laptop" },
  { value: "Server", label: "Server" },
  { value: "Printer", label: "Printer" },
  { value: "Network Switch", label: "Network Switch" },
  { value: "Others", label: "Others" }
];

// Asset Classification Type Option
export const CLASSIFICATION_TYPE_OPTION = [
  { value: "Confidential", label: "Confidential" },
  { value: "Internal", label: "Internal" },
  { value: "Public", label: "Public" },
  { value: "Highly Confidential (Secret)", label: "Highly Confidential (Secret)" }
]


// Vendor Type options
export const VENDOR_TYPE_OPTION = [
  { label: "Service Provider", value: "Service Provider" },
  { label: "Client Vendor", value: "Client Vendor" },
  { label: "Product Supplier", value: "Product Supplier" },
  { label: "Software Vendor", value: "Software Vendor" },
  { label: "Contractor", value: "Contractor" },
  { label: "Manufacturer", value: "Manufacturer" },
  { label: "Distributor", value: "Distributor" },
  { label: "Consultant", value: "Consultant" },
  { label: "Logistics Provider", value: "Logistics Provider" },
  { label: "Maintenance Provider", value: "Maintenance Provider" },
  { label: "Outsourcing Provider", value: "Outsourcing Provider" },
  { label: "Others", value: "Others" }
]

//Vendor Category Option
export const VENDOR_CATEGORY_OPTIONS = [
  { label: "Background checkers", value: "Background checkers" },
  { label: "CRM platforms", value: "CRM platforms" },
  { label: "Cloud providers", value: "Cloud providers" },
  { label: "Communication platforms", value: "Communication platforms" },
  { label: "Data warehouse providers", value: "Data warehouse providers" },
  { label: "Datastore providers", value: "Datastore providers" },
  { label: "Document management", value: "Document management" },
  { label: "Endpoint security", value: "Endpoint security" },
  { label: "HRMS", value: "HRMS" },
  { label: "Identity providers", value: "Identity providers" },
  { label: "Incident management", value: "Incident management" },
  { label: "MDM", value: "MDM" },
  { label: "Observability", value: "Observability" },
  { label: "Security training", value: "Security training" },
  { label: "Task management", value: "Task management" },
  { label: "Version control systems", value: "Version control systems" },
  { label: "Vulnerability scanners", value: "Vulnerability scanners" },
  { label: "Other", value: "Other" }
]

//Vendor Type Access Option
export const VENDOR_TYPE_ACCESS_OPTIONS = [
  { label: "Confidential Customer Data", value: "Confidential Customer Data" },
  { label: "Confidential Company Data (payroll, designs etc)", value: "Confidential Company Data (payroll, designs etc)" },
  { label: "Personal Data (email, phone, SSN etc) of employees and users", value: "Personal Data (email, phone, SSN etc) of employees and users" },
  { label: "Patient Health Information", value: "Patient Health Information" },
  { label: "Public Customer Data", value: "Public Customer Data" },
  { label: "Card holder data", value: "Card holder data" },
  { label: "Other non confidential information", value: "Other non confidential information" }
]

//Vendor Risk Level Option
export const VENDOR_RISK_LEVEL_OPTIONS = [
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
  { label: "None", value: "None" }
]

//Vendor Account Option
export const VENDOR_ACCOUNT_ACCESS_OPTIONS =
  [
    { label: "Admin", value: "Admin" },
    { label: "Editor", value: "Editor" },
    { label: "Viewer", value: "Viewer" },
    { label: "No Access", value: "No Access" }
  ]


//Vendor Account Option
export const DATA_CLASSIFICATION_ACCESS_OPTIONS =

  [
    { label: "Confidential", value: "Confidential" },
    { label: "Restricted", value: "Restricted" },
    { label: "Critical", value: "Critical" },
    { label: "Sensitive", value: "Sensitive" },
    { label: "Public", value: "Public" },
    { label: "Unclassified", value: "Unclassified" }
  ]


// Data collection Medium
export const DATA_COLLECTION_MEDIUM = [
  { label: "Digital / Soft Copy", value: "Digital / Soft Copy" },
  { label: "Physical / Hard Copy", value: "Physical / Hard Copy" },
  { label: "Others", value: "Others" }
]

// Data Acess Type Option

export const DATA_ACCESS_TYPE_OPTIONS = [
  { label: "Admin", value: "Admin" },
  { label: "Editor", value: "Editor" },
  { label: "Viewer", value: "Viewer" },
  { label: "No Access", value: "No Access" }
];

// Data collection Medium
export const DATA_TRANSFER_MODE_OPTIONS = [
  { label: "Physical", value: "Physical" },
  { label: "Electronic", value: "Electronic" },
]


// Organization Employee Size
export const ORGANIZATIONS_EMPLOYEE_OPTIONS = [
  { label: "<10", value: "<10" },
  { label: "11-20", value: "11-20" },
  { label: "21-50", value: "21-50" },
  { label: "51-99", value: "51-99" },
  { label: "100-200", value: "100-200" },
  { label: ">200", value: ">200" },
]

// Organization Annual Turnover
export const ORGANIZATIONS_ANNUAL_TURNOVER_OPTIONS = [
  { label: "Micro Enterprise (<$1mil)", value: "Micro Enterprise (&lt;$1mil)" },
  { label: "Small Enterprise ($1mil-$10mil)", value: "Small Enterprise ($1mil-S$10mil)" },
  { label: "Medium Enterprise ($10mil-S$100mil)", value: "Medium Enterprise (S$10mil-S$100mil)" },
  { label: "Large Enterprise (&gt;$100mil)", value: "Large Enterprise (&gt;$100mil)" },
]

// Users Employee Type
export const EMPLOYEE_TYPE_OPTIONS = [
  { label: "Full-Time Employees", field: "Full-Time Employees" },
  { label: "Part-Time Employees", field: "Part-Time Employees" },
  { label: "Contractors / Consultants", field: "Contractors / Consultants" },
  { label: "Temporary Employees", field: "Temporary Employees" },
  { label: "Interns", field: "Interns" },
  { label: "Others", field: "Others" },
]

// Users Permission options
export const USERS_PERMISSION_OPTIONS = [
  { label: "Tech Admin - Admin", value: "Tech Admin - Admin" },
  { label: "Tech Admin - Member", value: "Tech Admin - Member" },
  { label: "User - Admin", value: "User - Admin" },
  { label: "User - Management", value: "User - Management" },
  { label: "User - Vendor", value: "User - Vendor" },
  { label: "User - Member", value: "User - Member" },
  { label: "User - Guest", value: "User - Guest" },
  { label: "Consultant - Admin", value: "Consultant - Admin" },
  { label: "Consultant - Member", value: "Consultant - Member" },
  { label: "Auditor - Admin", value: "Auditor - Admin" },
  { label: "Auditor - Member", value: "Auditor - Member" },
  { label: "Regulator - Admin", value: "Regulator - Admin" },
  { label: "Regulator - Member", value: "Regulator - Member" }
]

// Focus Mode: Applicable Group Types (for group-based permissions)
// Only groups of type "Roles" and "Appointment" are used for Focus Mode permissions
export const FOCUS_MODE_GROUP_TYPES = {
  ROLES: 'Roles',
  APPOINTMENT: 'Appointment'
};

// Focus Mode: Group Type Configuration
export const FOCUS_MODE_GROUP_TYPE_CONFIG = {
  'Roles': {
    icon: 'AdminPanelSettingsIcon',
    color: '#D32F2F',
    bgColor: '#FEF2F2',
    description: 'Official roles and responsibilities within the organization',
    emoji: '🎯',
    examples: ['Data Protection Officer', 'Chief Information Security Officer', 'Compliance Officer', 'Risk Manager']
  },
  'Appointment': {
    icon: 'AssignmentIndIcon',
    color: '#2E7D32',
    bgColor: '#F0FDF4',
    description: 'Leadership positions and executive appointments',
    emoji: '👔',
    examples: ['CEO', 'CTO', 'CFO', 'COO', 'Leadership Team', 'Board Member']
  }
};

// Focus Mode: Permission Type Options
export const FOCUS_MODE_PERMISSION_TYPES = [
  { value: 'role', label: 'Role-Based', icon: 'BadgeIcon', color: '#1D3DFC', description: 'Permissions based on user role' },
  { value: 'group', label: 'Group-Based', icon: 'GroupsIcon', color: '#059669', description: 'Permissions based on user groups (Roles & Appointments)' }
];

// Resource Entity Type Options for ResourceDefaults
export const RESOURCE_ENTITY_TYPE_OPTIONS = [
  { 
    value: 'asset_software', 
    label: 'Software Assets', 
    colors: { bg: '#EBF8FF', text: '#1E40AF' } 
  },
  { 
    value: 'asset_hardware', 
    label: 'Hardware Assets', 
    colors: { bg: '#F3E8FF', text: '#7C3AED' } 
  },
  { 
    value: 'vendor', 
    label: 'Vendors', 
    colors: { bg: '#DCFCE7', text: '#166534' } 
  },
  { 
    value: 'vendor_account', 
    label: 'Vendor Accounts', 
    colors: { bg: '#FEF3C7', text: '#92400E' } 
  },
  { 
    value: 'data_inventory', 
    label: 'Data Inventory', 
    colors: { bg: '#DBEAFE', text: '#1E40AF' } 
  },
  { 
    value: 'governance', 
    label: 'Governance', 
    colors: { bg: '#F3E8FF', text: '#6B21A8' } 
  }
];

// Resource Status Options for ResourceDefaults  
export const RESOURCE_STATUS_OPTIONS = [
  { 
    value: true, 
    label: 'Active', 
    colors: { bg: '#DCFCE7', text: '#166534' } 
  },
  { 
    value: false, 
    label: 'Inactive', 
    colors: { bg: '#FEE2E2', text: '#DC2626' } 
  }
];



// Projects Estimated Duration

export const PROJECTS_ESTIMATED_DURATION = [
  { label: "3 months", value: "3 months" },
  { label: "6 months", value: "6 months" },
  { label: "9 months", value: "9 months" },
  { label: "12 months", value: "12 months" }
]


// Projects Status Option
export const PROJECTS_STATUS_OPTIONS = [
  { label: "Basic Setup", value: "Basic Setup" },
  { label: "Implementation", value: "Implementation" },
  { label: "Review", value: "Review" },
  { label: "Remediation", value: "Remediation" },
  { label: "Audit & Signoff", value: "Audit & Signoff" },
  { label: "Monitoring", value: "Monitoring" },
  { label: "Maintenance", value: "Maintenance" },
  { label: "Evaluation", value: "Evaluation" },
  { label: "Optimization", value: "Optimization" },
  { label: "Renewal", value: "Renewal" }
];

// Risk Type Options
export const RISK_TYPE_OPTIONS = [
  { value: "Infrastructure", label: "Infrastructure" },
  { value: "Regulatory and Compliance", label: "Regulatory and Compliance" },
  { value: "Human Factor", label: "Human Factor" },
  { value: "Physical Security", label: "Physical Security" },
  { value: "Supply Chain", label: "Supply Chain" },
  { value: "Data Breach", label: "Data Breach" }
];

// Risk Category Options
export const RISK_CATEGORY_OPTIONS = [
  { value: "Classical/Standard", label: "Classical/Standard" },
  { value: "Cloud", label: "Cloud" },
  { value: "OT", label: "OT" },
  { value: "AI", label: "AI" },
  { value: "Others", label: "Others" },
];

// Risk Impact Options
export const RISK_IMPACT_OPTIONS = [
  { value: "5 - Major", label: "5 - Major" },
  { value: "4 - Serious", label: "4 - Serious" },
  { value: "3 - Significant", label: "3 - Significant" },
  { value: "2 - Moderate", label: "2 - Moderate" },
  { value: "1 - Minor", label: "1 - Minor" }
];

// Risk Likelihood Options
export const RISK_LIKELIHOOD_OPTIONS = [
  { value: "5 - Highly Likely", label: "5 - Highly Likely" },
  { value: "4 - Likely", label: "4 - Likely" },
  { value: "3 - Possible", label: "3 - Possible" },
  { value: "2 - Unlikely", label: "2 - Unlikely" },
  { value: "1 - Rare", label: "1 - Rare" }
];

// Risk Assessment Type Options
export const RISK_ASSESSMENT_TYPE_OPTIONS = [
  { value: "Inherent", label: "Inherent" },
  { value: "Residual", label: "Residual" }
];

// Risk Decision Options (Risk Remediation)
export const RISK_DECISION_OPTIONS = [
  { value: "Accept", label: "Accept" },
  { value: "Avoid", label: "Avoid" },
  { value: "Mitigated", label: "Mitigated" },
  { value: "Transfer", label: "Transfer" }
];

// Current Implementation Status Options (Risk Remediation)
export const CURRENT_IMPLEMENTATION_STATUS_OPTIONS = [
  { value: "Not Started", label: "Not Started" },
  { value: "In Progress", label: "In Progress" },
  { value: "Hold", label: "Hold" },
  { value: "Completed", label: "Completed" }
];

// Control Tier Options
export const CONTROL_TIER_OPTIONS = [
  { value: "Essential", label: "Essential" },
  { value: "Supporter", label: "Supporter" },
  { value: "Practitioner", label: "Practitioner" },
  { value: "Promoter", label: "Promoter" },
  { value: "Performer", label: "Performer" },
  { value: "Advocate", label: "Advocate" }
];

// Requirement Type Options
export const REQUIREMENT_TYPE_OPTIONS = [
  { value: true, label: "SHALL" },
  { value: false, label: "SHOULD" }
];

// Risk Level Options
export const RISK_LEVEL_OPTIONS = [
  { value: "Low Risk", label: "Low Risk" },
  { value: "Medium Risk", label: "Medium Risk" },
  { value: "High Risk", label: "High Risk" }
];

// Review Frequency Options
export const REVIEW_FREQUENCY_OPTIONS = [
  { value: "Annual", label: "Annual" },
  { value: "Bi-Annual", label: "Bi-Annual" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Monthly", label: "Monthly" }
];

// ─── IP Asset Register — Related Documents groupings ─────────────────────────
// Maps related_model_type (the value stored in policy_center.related_model_type)
// to a human-readable group label used in the "Related Documents" tab.
export const RELATED_MODEL_GROUPS = {
  // IP Asset Register entity types
  brand_asset:       { label: 'Brand Assets',           order: 1 },
  trademark:         { label: 'Trademarks',             order: 2 },
  patent:            { label: 'Patents',                order: 3 },
  copyright:         { label: 'Copyrights',             order: 4 },
  registered_design: { label: 'Registered Designs',     order: 5 },
  idd:               { label: 'IDDs',                    order: 6 },
  trade_secret:      { label: 'Trade Secrets',          order: 7 },
  ip_database:       { label: 'Databases',              order: 8 },
  domain_name:       { label: 'Domain Names',           order: 9 },
  social_presence:   { label: 'Social Media Presence',  order: 10 },
  storefront:        { label: 'Storefronts / Platforms',order: 11 },
  third_party_right: { label: '3rd Party Rights',       order: 12 },
  infrastructure:    { label: 'Infrastructure',         order: 13 },
  contact:           { label: 'Contacts',               order: 14 },
  // Asset Management + Vendor entity types
  asset_hardware:    { label: 'Hardware Assets',        order: 15 },
  asset_software:    { label: 'Software Assets',        order: 16 },
  datainventory:     { label: 'Data Inventory',         order: 17 },
  vendor:            { label: 'Vendors',                order: 18 },
  vendor_account:    { label: 'Vendor Accounts',        order: 19 },
};

/**
 * getRelatedModelGroup — return the group label for a policy_center row that
 * has a related_model_type set.  Falls back to the raw model type string if
 * the type is not yet registered in RELATED_MODEL_GROUPS.
 * @param {object} row — a policy_center row
 * @returns {string}
 */
export function getRelatedModelGroup(row) {
  const type = row?.related_model_type;
  if (!type) return 'Other';
  return RELATED_MODEL_GROUPS[type]?.label ?? type;
}

/**
 * buildDocumentHierarchy — shared grouping helper extracted from the three
 * DocumentCenter view components (FolderTree, FolderView, IconView).
 *
 * @param {object[]} data — flat array of policy_center rows
 * @param {object}   opts
 * @param {'category'|'related_model'} opts.groupBy
 *   'category'      → groups by getCategoryFromType → DOCUMENT_CATEGORY_TYPE_MAP
 *   'related_model' → groups by related_model_type → RELATED_MODEL_GROUPS
 * @returns {{ [groupKey: string]: { label: string, items: object[] } }}
 */
export function buildDocumentHierarchy(data = [], { groupBy = 'category' } = {}) {
  const result = {};

  for (const row of data) {
    let key;
    let label;
    if (groupBy === 'related_model') {
      key = row.related_model_type || 'Other';
      label = getRelatedModelGroup(row);
    } else {
      // default: group by document category (Policy / Evidence)
      key = getCategoryFromType(row.type);
      label = key;
    }

    if (!result[key]) {
      result[key] = { label, items: [] };
    }
    result[key].items.push(row);
  }

  // Sort groups by RELATED_MODEL_GROUPS order if using related_model mode
  if (groupBy === 'related_model') {
    const sorted = Object.entries(result).sort(([a], [b]) => {
      const oa = RELATED_MODEL_GROUPS[a]?.order ?? 999;
      const ob = RELATED_MODEL_GROUPS[b]?.order ?? 999;
      return oa - ob;
    });
    return Object.fromEntries(sorted);
  }

  return result;
}